import React, {useRef, useEffect, useState} from 'react';
import { Stack, Typography, Box } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import PatientCard from '../display/PatientCard';
import { useHistoryEntryTypes } from '../../../hooks/history/useHistoryEntryTypes';
import DefaultHistoryEntryTypesSeederDialog from './DefaultHistoryEntryTypesSeederDialog';
import PatientHistoryList from '../lists/PatientHistoryList';
import ProceduresList from '../lists/ProceduresList';
import UpcomingAppointmentsList from '../lists/UpcomingAppointmentsList';
import VitalsList from '../lists/VitalsList';
import AttachmentsList from '../lists/AttachmentsList';
import { getFilesByPetId } from '../../../api/uploadService';
import RequirePermission from '../../RequirePermission';
import { SUB_MODULES } from '../../../constants/subModulesConstants';
import RecommendedVisitsList from '../lists/RecommendedVisitsList';

export default function PatientSummary({ open, onClose, pet = null }) {

    const childRef = useRef();

    const handleClose = () => onClose();

    const [seederDialogOpen, setSeederDialogOpen] =  React.useState(false);

    const [oldRecords, setOldRecords] = useState(null);

    const fetchOldRecords = async () => {
      const res = await getFilesByPetId(pet.id, 1, 100, "oldRecords");
      setOldRecords(res);
    };

    const onEmptyData = () => {
        setSeederDialogOpen(true);
    };
    
    const { historyEntryTypes, loading, setHistoryEntryTypes } = useHistoryEntryTypes(null, onEmptyData);

    const [appointmentsRefreshKey, setAppointmentsRefreshKey] = useState(0);

    const refreshUpcomingAppointments = () => {
      // Increment the key to force re-mount
      setAppointmentsRefreshKey(prev => prev + 1);
    };

    const [recommendedVisitsRefreshKey, setRecommendedVisitsRefreshKey] = useState(0);
    
    const refreshRecommendedVisits = () => {
      // Increment the key to force re-mount
      setRecommendedVisitsRefreshKey(prev => prev + 1);
    };

    React.useEffect(() => {
      if (open && pet)
      {
        fetchOldRecords();
      }
    }, [open, pet]);

    if (!pet) {
        return (
            <></>
        );
    }

    return (
        <React.Fragment>
        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
              <>
              <Typography variant="h6" component="span" sx={{ mb: 0 }}>Patient Medical History</Typography>
              <br />
              <Typography variant="h6" component="span" sx={{ mb: 0 }}>{`${pet.name} (${pet.owner.first_name} ${pet.owner.last_name})`}</Typography>
              </>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <RequirePermission subModuleName={SUB_MODULES.PETS} minAccessLevel={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <PatientCard pet={pet}/>
                  </Box>
                </RequirePermission>
                <RequirePermission subModuleName={SUB_MODULES.HISTORY} minAccessLevel={1}>

                  <PatientHistoryList 
                      ref={childRef}
                      pet={pet}
                  />
                </RequirePermission>
                <RequirePermission subModuleName={SUB_MODULES.APPOINTMENTS} minAccessLevel={1}>
                  <UpcomingAppointmentsList 
                    key={appointmentsRefreshKey}
                    refreshRecommendedVisits={refreshRecommendedVisits}
                    pet={pet}
                    mode='pet'
                  />
                </RequirePermission>
                <RequirePermission subModuleName={SUB_MODULES.APPOINTMENTS} minAccessLevel={1}>
                  <RecommendedVisitsList 
                    key={recommendedVisitsRefreshKey}
                    refreshUpcomingAppointments={refreshUpcomingAppointments}
                    pet={pet}
                    mode='pet'
                  />
                </RequirePermission>
                <RequirePermission subModuleName={SUB_MODULES.DOCUMENTATION} minAccessLevel={1}>
                  <VitalsList 
                    pet={pet}
                  />
                </RequirePermission>
                <RequirePermission subModuleName={SUB_MODULES.ATTACHMENTS} minAccessLevel={1}>
                  <AttachmentsList 
                    title='Old Records'
                    tags={["oldRecords"]}
                    fetchData={fetchOldRecords}
                    pet={pet}
                    attachments={oldRecords?.data || []}
                  />
                </RequirePermission>
              </Stack>
            </DialogContent>
            <DialogActions>
              <RequirePermission subModuleName={SUB_MODULES.HISTORY} minAccessLevel={2}>
                <Button onClick={() => childRef.current?.handleCreateHistory()}>New Med. Hist.</Button>
              </RequirePermission>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <DefaultHistoryEntryTypesSeederDialog
            open={seederDialogOpen}
            setOpen={setSeederDialogOpen} 
        />
        </React.Fragment>
    );
}